import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { solanaRecipientInboxKey } from "../../joinsplit-v2/solana/recipient";
import {
  fetchPrivateHistoryEntries,
  storePrivateHistoryEntry,
  storePrivateInboxNote,
} from "../../api/private-data-storage";
import {
  findSolanaToken,
  formatSolanaTokenAmount,
  type SolanaNetworkConfig,
} from "../../config/solana";
import type { JoinSplitNote, StoredJoinSplitNote } from "../../joinsplit/types";
import {
  createIndexedDbSolanaLeafCache,
  createSolanaRelayClient,
  activeSolanaJournalRecords,
  completeSolanaJournalWithoutDeliveries,
  getSolanaJournalRecord,
  persistSolanaJournalBackup,
  restoreSolanaJournalBackups,
  isSolanaFlatNote,
  restoreSolanaInboxNamespace,
  listSolanaJournal,
  markSolanaJournalDeliveryCompleted,
  reconcileSolanaJournal,
  solanaNoteMint,
  synchronizeSolanaNotes,
  updateSolanaJournalRecord,
  type SolanaJournalRecord,
  type SolanaJournalBackupVariant,
  type SolanaJournalRestoreResult,
  type SolanaJournalScope,
  type SolanaRpc,
} from "../../joinsplit-v2/solana";
import { formatMpkHex, hashMpkHex } from "../../utils/format";
import {
  decryptInboxNote,
  encryptInboxNoteWithPublicKey,
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
} from "../../utils/inbox-crypto";
import {
  loadSolanaHistoryCache,
  loadSolanaNotesCache,
  saveSolanaHistoryCache,
  saveSolanaNotesCache,
  type SolanaCacheScope,
} from "../storage";
import type {
  SolanaHistoryEntry,
  SolanaLogType,
  SolanaWalletState,
} from "../types";
import type { SolanaProxyAuthRequestOptions } from "./useSolanaProxyAuth";
import { withReadCancellation } from "../../joinsplit-v2/readCancellation";
import { assertNotAborted } from "../../joinsplit-v2/solana/common";
import { withDeadline } from "../../utils/withDeadline";
import { getDirectSolanaRecoveryRpc } from "../../joinsplit-v2/solana/rpc";
import { recoverSolanaNotesFromChain } from "../../joinsplit-v2/solana/onchainRecovery";

function buildEngineUrl(basePath: string, endpoint: string): string {
  if (basePath.includes("?path=")) {
    return `${basePath}${endpoint.replace(/^\//, "")}`;
  }
  return `${basePath.replace(/\/+$/, "")}${endpoint}`;
}

export interface SolanaBalanceRow {
  mint: string | null;
  symbol: string;
  decimals: number;
  spendable: bigint;
  pending: bigint;
  spendableDisplay: string;
  pendingDisplay: string;
}

function historyKey(
  entry: Omit<SolanaHistoryEntry, "id" | "timestamp">,
): string {
  return `${(entry.txHash || "").toLowerCase()}|${entry.type}|${entry.amount}|${entry.symbol}`;
}

function mergeNotes(
  current: StoredJoinSplitNote[],
  incoming: StoredJoinSplitNote[],
): StoredJoinSplitNote[] {
  const byCommitment = new Map<string, StoredJoinSplitNote>();
  for (const stored of current) {
    byCommitment.set(stored.note.commitment.toLowerCase(), stored);
  }
  for (const stored of incoming) {
    const key = stored.note.commitment.toLowerCase();
    const prior = byCommitment.get(key);
    // A spent record stays spent; an authenticated leaf wins over a mailbox
    // claim; otherwise the newer record wins.
    if (prior?.spent && !stored.spent) continue;
    if (
      prior &&
      prior.note.leafIndex >= 0 &&
      stored.note.leafIndex < 0 &&
      !stored.spent
    ) {
      continue;
    }
    byCommitment.set(key, stored);
  }
  return [...byCommitment.values()];
}

function settlementIntentIdentity(record: SolanaJournalRecord): string {
  if (!record.intent) return "missing";
  return (
    record.intent.payload ||
    JSON.stringify(record.intent, (_key, value) =>
      typeof value === "bigint" ? value.toString() : value,
    )
  );
}

export function useSolanaNotes({
  network,
  rpc,
  wallet,
  ensureProxyAuthToken,
  authHeadersForPath,
  clearProxyAuthToken,
  addLog,
}: {
  network: SolanaNetworkConfig;
  rpc: SolanaRpc;
  wallet: SolanaWalletState;
  ensureProxyAuthToken: (
    options?: SolanaProxyAuthRequestOptions,
  ) => Promise<string>;
  authHeadersForPath: (
    path: string,
    options?: SolanaProxyAuthRequestOptions,
  ) => Promise<Record<string, string>>;
  clearProxyAuthToken: () => void;
  addLog: (message: string, type?: SolanaLogType, signature?: string) => void;
}) {
  const [notes, setNotes] = useState<StoredJoinSplitNote[]>([]);
  const [history, setHistory] = useState<SolanaHistoryEntry[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [pendingSubmissions, setPendingSubmissions] = useState<
    SolanaJournalRecord[]
  >([]);
  const [conflictedSubmissionIds, setConflictedSubmissionIds] = useState<
    string[]
  >([]);
  const [conflictedSubmissionOptions, setConflictedSubmissionOptions] =
    useState<Record<string, SolanaJournalBackupVariant[]>>({});
  const notesRef = useRef<StoredJoinSplitNote[]>([]);
  const historyRef = useRef<SolanaHistoryEntry[]>([]);
  const addLogRef = useRef(addLog);
  addLogRef.current = addLog;
  const cache = useMemo(() => createIndexedDbSolanaLeafCache(), []);
  const notesApiUrl = network.engineProxyUrl;
  const spendingKeys = wallet.spendingKeys;

  const cacheScope = useMemo<SolanaCacheScope | null>(
    () =>
      spendingKeys && wallet.address
        ? {
            networkKey: network.key,
            programId: network.programId,
            address: wallet.address,
            spendingKey: spendingKeys.spendingKey,
          }
        : null,
    [network.key, network.programId, spendingKeys, wallet.address],
  );
  const journalScope = useMemo<SolanaJournalScope | null>(
    () =>
      spendingKeys && wallet.address
        ? {
            network: network.key,
            programId: network.programId,
            account: wallet.address,
            spendingKey: spendingKeys.spendingKey,
          }
        : null,
    [network.key, network.programId, spendingKeys, wallet.address],
  );
  // Each connection has its own identity, including A -> B -> A transitions.
  // Async work must check the live identity, never its captured wallet prop.
  const context = useMemo(
    () => ({
      scope: cacheScope,
      ready: Promise.resolve(),
      syncing: false,
      syncFinished: Promise.resolve(),
      historySync: 0,
    }),
    [cacheScope],
  );
  const contextRef = useRef(context);
  if (contextRef.current !== context) {
    contextRef.current = context;
    notesRef.current = [];
    historyRef.current = [];
  }
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isCurrent = useCallback(
    () => mounted.current && contextRef.current === context,
    [context],
  );
  const assertCurrent = useCallback(() => {
    if (!isCurrent()) throw new Error("Solana wallet or network changed");
  }, [isCurrent]);
  const relay = useMemo(() => {
    const create = (interactive: boolean) =>
      createSolanaRelayClient({
        engineProxyUrl: network.relayProxyUrl,
        relayPath: network.relayPath,
        authHeaders: async () => {
          assertCurrent();
          const token = await ensureProxyAuthToken({ interactive });
          assertCurrent();
          return { Authorization: `Bearer ${token}` };
        },
      });
    return create(true);
  }, [
    assertCurrent,
    ensureProxyAuthToken,
    network.relayProxyUrl,
    network.relayPath,
  ]);

  const backupJournal = useCallback(
    async (
      record: SolanaJournalRecord,
      options: { interactive?: boolean; signal?: AbortSignal } = {
        interactive: true,
      },
    ) => {
      if (!spendingKeys || !journalScope) {
        throw new Error(
          "Connect the Solana private account before backing up notes",
        );
      }
      assertCurrent();
      const recipientKey = await hashMpkHex(formatMpkHex(spendingKeys.mpk));
      const path = `/api/v1/recovery/journal/${recipientKey}`;
      const headers = await withReadCancellation(
        () =>
          authHeadersForPath(path, {
            interactive: options.interactive === true,
            expectedRecipientKey: recipientKey,
          }),
        options.signal,
      );
      assertCurrent();
      assertNotAborted(options.signal, "before recovery backup");
      await persistSolanaJournalBackup(journalScope, record.id, record, {
        baseUrl: notesApiUrl,
        headers,
        signal: options.signal,
      });
      assertCurrent();
    },
    [
      assertCurrent,
      authHeadersForPath,
      journalScope,
      notesApiUrl,
      spendingKeys,
    ],
  );

  const restoreJournal =
    useCallback(async (): Promise<SolanaJournalRestoreResult> => {
      if (!spendingKeys || !journalScope)
        return { restored: 0, quarantined: 0, variants: new Map() };
      assertCurrent();
      const recipientKey = await hashMpkHex(formatMpkHex(spendingKeys.mpk));
      const path = `/api/v1/recovery/journal/${recipientKey}`;
      const headers = await authHeadersForPath(path, {
        interactive: false,
        expectedRecipientKey: recipientKey,
      });
      assertCurrent();
      const result = await restoreSolanaJournalBackups(journalScope, {
        baseUrl: notesApiUrl,
        headers,
      });
      assertCurrent();
      return result;
    }, [
      assertCurrent,
      authHeadersForPath,
      journalScope,
      notesApiUrl,
      spendingKeys,
    ]);

  const commitNotes = useCallback(
    async (
      updater: (current: StoredJoinSplitNote[]) => StoredJoinSplitNote[],
    ) => {
      await context.ready;
      assertCurrent();
      if (!cacheScope) throw new Error("Solana wallet is disconnected");
      const next = updater(notesRef.current);
      notesRef.current = next;
      setNotes(next);
      await saveSolanaNotesCache(cacheScope, next);
      assertCurrent();
      return next;
    },
    [assertCurrent, cacheScope, context],
  );

  const rememberLocalNotes = useCallback(
    async (incoming: JoinSplitNote[], signature?: string) => {
      if (incoming.length === 0) return;
      await commitNotes((current) =>
        mergeNotes(
          current,
          incoming.map((note) => ({
            note,
            timestamp: Date.now(),
            txHash: signature,
            spent: false,
          })),
        ),
      );
    },
    [commitNotes],
  );

  const rememberSettlement = useCallback(
    async (
      outputs: JoinSplitNote[],
      spentCommitments: string[],
      signature?: string,
    ) => {
      const spent = new Set(
        spentCommitments.map((value) => value.toLowerCase()),
      );
      await commitNotes((current) =>
        mergeNotes(
          current,
          outputs.map((note) => ({
            note,
            timestamp: Date.now(),
            txHash: signature,
            spent: false,
          })),
        ).map((stored) =>
          spent.has(stored.note.commitment.toLowerCase())
            ? { ...stored, spent: true }
            : stored,
        ),
      );
    },
    [commitNotes],
  );

  const markSpentLocally = useCallback(
    async (commitments: string[]) => {
      if (commitments.length === 0) return;
      const spent = new Set(commitments.map((value) => value.toLowerCase()));
      await commitNotes((current) =>
        current.map((stored) =>
          spent.has(stored.note.commitment.toLowerCase())
            ? { ...stored, spent: true }
            : stored,
        ),
      );
    },
    [commitNotes],
  );

  const commitHistory = useCallback(
    async (entries: SolanaHistoryEntry[]) => {
      assertCurrent();
      const sorted = [...entries]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 200);
      historyRef.current = sorted;
      setHistory(sorted);
      if (cacheScope) await saveSolanaHistoryCache(cacheScope, sorted);
      assertCurrent();
    },
    [assertCurrent, cacheScope],
  );

  const addHistoryEntry = useCallback(
    async (
      entry: Omit<SolanaHistoryEntry, "id" | "timestamp">,
      recoveredTimestamp?: number,
    ) => {
      await context.ready;
      assertCurrent();
      const local: SolanaHistoryEntry = {
        ...entry,
        id: Date.now(),
        timestamp: recoveredTimestamp ?? Date.now(),
      };
      const key = historyKey(entry);
      await commitHistory([
        local,
        ...historyRef.current.filter(
          (existing) => historyKey(existing) !== key,
        ),
      ]);
      if (!spendingKeys) return;
      try {
        const mpkHex = formatMpkHex(spendingKeys.mpk);
        const recipient = await hashMpkHex(mpkHex);
        assertCurrent();
        const headers = await authHeadersForPath("/api/v1/history/store", {
          expectedRecipientKey: recipient,
        });
        assertCurrent();
        await withDeadline(
          () =>
            storePrivateHistoryEntry({
              baseUrl: notesApiUrl,
              headers,
              entry: {
                recipient,
                type: entry.type,
                amount: entry.amount,
                token: entry.symbol,
                memo: entry.memo,
                txHash: entry.txHash || "",
                direction: entry.type === "receive" ? "in" : "out",
                network: network.key,
                ...(recoveredTimestamp === undefined
                  ? {}
                  : { timestamp: recoveredTimestamp }),
              },
              encryptTarget: {
                inboxPublicKeyHex: formatInboxPublicKeyHex(
                  spendingKeys.spendingKey,
                ),
              },
            }),
          "History backup timed out; entry is kept locally",
          undefined,
          30_000,
        );
      } catch (error) {
        if (!isCurrent()) return;
        addLog(
          `History entry kept locally only: ${error instanceof Error ? error.message : String(error)}`,
          "warning",
        );
      }
    },
    [
      addLog,
      assertCurrent,
      context,
      isCurrent,
      authHeadersForPath,
      commitHistory,
      network.key,
      notesApiUrl,
      spendingKeys,
    ],
  );

  /** Deliver every finalized output note of a journal record to its inbox. */
  const completeDeliveries = useCallback(
    async (
      records: SolanaJournalRecord[],
      options: { interactive?: boolean; signal?: AbortSignal } = {
        interactive: true,
      },
    ) => {
      if (!spendingKeys || !journalScope) {
        return { pendingRecordIds: records.map((record) => record.id) };
      }
      assertCurrent();
      const pendingRecordIds: string[] = [];
      const selfMpkHex = formatMpkHex(spendingKeys.mpk).toLowerCase();
      const selfInbox = formatInboxPublicKeyHex(spendingKeys.spendingKey);
      for (const original of records) {
        if (
          original.deliveries.length === 0 &&
          original.outputNotes.length === 0
        ) {
          await completeSolanaJournalWithoutDeliveries(
            journalScope,
            original.id,
            original,
          );
          continue;
        }
        let record = original;
        for (const delivery of original.deliveries) {
          assertCurrent();
          const current = record.deliveries.find(
            (candidate) =>
              candidate.commitment.toLowerCase() ===
              delivery.commitment.toLowerCase(),
          );
          if (!current || current.disposition) continue;
          const note = record.outputNotes.find(
            (candidate) =>
              candidate.commitment.toLowerCase() ===
              delivery.commitment.toLowerCase(),
          );
          if (!note || note.leafIndex < 0) continue;
          if (current.keepLocal)
            await rememberLocalNotes([note], record.signature);
          const isSelf = current.recipientMpkHex.toLowerCase() === selfMpkHex;
          const inboxPublicKey =
            current.recipientInboxPublicKeyHex ||
            (isSelf ? selfInbox : undefined);
          const usableInbox = solanaRecipientInboxKey(inboxPublicKey);
          if (!usableInbox) {
            // Legacy records must retain the only copy of the recipient's note.
            record = await updateSolanaJournalRecord(
              journalScope,
              record.id,
              {
                state: "delivery_pending",
                error:
                  "Recipient inbox public key is missing or invalid; note delivery is pending",
              },
              record,
            );
            addLog(record.error!, "warning", record.signature);
            continue;
          }
          try {
            assertNotAborted(options.signal, "before encrypted note delivery");
            const deliveredNote = note.privateHistory
              ? {
                  ...note,
                  privateHistory: {
                    ...note.privateHistory,
                    txHash: record.signature,
                  },
                }
              : note;
            let encryptedNote = current.encryptedNote;
            if (!encryptedNote) {
              encryptedNote = await encryptInboxNoteWithPublicKey(
                deliveredNote,
                usableInbox,
              );
              assertCurrent();
              record = await updateSolanaJournalRecord(
                journalScope,
                record.id,
                {
                  deliveries: record.deliveries.map((candidate) =>
                    candidate.commitment.toLowerCase() ===
                    note.commitment.toLowerCase()
                      ? { ...candidate, encryptedNote }
                      : candidate,
                  ),
                },
                record,
              );
            }
            const recipientKey = await hashMpkHex(current.recipientMpkHex);
            assertCurrent();
            const headers = await withReadCancellation(
              () =>
                authHeadersForPath("/api/v1/notes/store", {
                  interactive: options.interactive === true,
                  ...(isSelf ? { expectedRecipientKey: recipientKey } : {}),
                }),
              options.signal,
            );
            assertCurrent();
            // Only the idempotent HTTP delivery is bounded. The encrypted
            // delivery record above and the disposition below remain awaited.
            await withDeadline(
              (signal) =>
                storePrivateInboxNote({
                  baseUrl: notesApiUrl,
                  recipientKey,
                  headers,
                  encryptedNote: encryptedNote!,
                  commitment: note.commitment,
                  treeIndex: note.treeIndex,
                  signal,
                }),
              "Encrypted note delivery timed out",
              options.signal,
              30_000,
            );
            assertCurrent();
            assertNotAborted(options.signal, "after encrypted note delivery");
            record = await markSolanaJournalDeliveryCompleted(
              journalScope,
              record.id,
              note.commitment,
              "delivered",
              undefined,
              record,
            );
          } catch (error) {
            if (!isCurrent()) {
              return {
                pendingRecordIds: records.map((candidate) => candidate.id),
              };
            }
            const reason =
              error instanceof Error ? error.message : String(error);
            record = await updateSolanaJournalRecord(
              journalScope,
              record.id,
              {
                state: "delivery_pending",
                error: `Note delivery is pending: ${reason}`,
              },
              record,
            );
            addLog(
              `Note delivery is pending and will be retried: ${reason}`,
              "warning",
              record.signature,
            );
            if (options.signal?.aborted) {
              return {
                pendingRecordIds: records.map((candidate) => candidate.id),
              };
            }
          }
        }
        const latest = await getSolanaJournalRecord(journalScope, original.id);
        if (!latest || latest.state !== "completed") {
          pendingRecordIds.push(original.id);
        }
      }
      return { pendingRecordIds };
    },
    [
      addLog,
      assertCurrent,
      isCurrent,
      authHeadersForPath,
      journalScope,
      notesApiUrl,
      rememberLocalNotes,
      spendingKeys,
    ],
  );

  const syncNotes = useCallback(
    async (
      resumeRecordId?: string,
      resumeBackupId?: string,
      recoveryOptions: { forDeposit?: boolean; mint?: string | null } = {},
    ) => {
      if (recoveryOptions.forDeposit) {
        while (context.syncing) await context.syncFinished;
        assertCurrent();
      }
      if (
        !spendingKeys ||
        !wallet.address ||
        !journalScope ||
        !isCurrent() ||
        context.syncing
      )
        return;
      context.syncing = true;
      let finishSync!: () => void;
      context.syncFinished = new Promise<void>((resolve) => {
        finishSync = resolve;
      });
      setSyncing(true);
      addLog(
        "Recovery started: checking encrypted backups and private notes",
        "info",
      );
      try {
        await context.ready;
        assertCurrent();
        if (network.onchainRecoveryEnabled) {
          addLog(
            "Reading encrypted recovery data directly from Solana",
            "info",
          );
          const directRpc = getDirectSolanaRecoveryRpc(network);
          const recovered = await recoverSolanaNotesFromChain({
            rpc: directRpc,
            network,
            spendingKeys,
            cache,
          });
          assertCurrent();
          // Current local attempts may not be finalized yet. Preserve them,
          // but admit spendable notes only after the independent chain scan.
          const candidates = mergeNotes(notesRef.current, [
            ...recovered.validated.values(),
          ]);
          const checked = await synchronizeSolanaNotes({
            rpc: directRpc,
            network,
            spendingKeys,
            notes: candidates,
            cache,
          });
          assertCurrent();
          await commitNotes((current) =>
            mergeNotes(
              current,
              candidates.map((stored) => {
                const key = stored.note.commitment.toLowerCase();
                if (checked.spent.has(key) || recovered.spent.has(key))
                  return { ...stored, spent: true };
                return (
                  checked.validated.get(key) ?? {
                    ...stored,
                    note: { ...stored.note, leafIndex: -1 },
                  }
                );
              }),
            ),
          );
          const available = [...checked.validated.values()].filter(
            (stored) => !stored.spent,
          );
          addLog(
            `On-chain recovery complete: ${available.length} available private note${available.length === 1 ? "" : "s"}; ${checked.pending.size} pending; ${checked.spent.size} spent. No recovery service was used.`,
            "success",
          );
          // Local intent bookkeeping is auxiliary to phrase-only chain recovery.
          // A damaged/unavailable journal must not prevent recovered notes from
          // becoming visible. Explicit resubmission still reports its failure.
          const reconciled = await reconcileSolanaJournal({
            rpc: directRpc,
            network,
            scope: journalScope,
            ...(resumeRecordId ? { relay, resumeRecordId } : {}),
            onLog: (...args) => {
              if (isCurrent()) addLog(...args);
            },
          }).catch((error) => {
            if (resumeRecordId) throw error;
            if (isCurrent())
              addLog(
                "Local transaction status could not be refreshed; on-chain notes were recovered.",
                "warning",
              );
            return {
              finalizedNotes: [],
              spentCommitments: [],
              deliveryPending: [],
              failed: [],
              stillPending: [],
              watchOnly: [],
            };
          });
          assertCurrent();
          if (
            recoveryOptions.forDeposit &&
            notesRef.current.some(
              ({ note, spent }) =>
                !spent &&
                note.leafIndex < 0 &&
                solanaNoteMint(note) === (recoveryOptions.mint ?? null),
            )
          )
            throw new Error(
              "Deposit paused: a note is still awaiting chain finality. Refresh recovery before retrying.",
            );
          return { ...reconciled, notes: [...notesRef.current] };
        }
        let recovery: SolanaJournalRestoreResult = {
          restored: 0,
          quarantined: 0,
          variants: new Map(),
        };
        try {
          recovery = await restoreJournal();
          if (recovery.restored > 0) {
            addLog(
              `Restored ${recovery.restored} encrypted Solana recovery record${recovery.restored === 1 ? "" : "s"}`,
              "info",
            );
          }
          if (recovery.quarantined > 0) {
            addLog(
              `Ignored ${recovery.quarantined} invalid Solana recovery backup${recovery.quarantined === 1 ? "" : "s"}; valid encrypted records remain available`,
              "warning",
            );
          }
        } catch (error) {
          if (!isCurrent()) return;
          if (resumeRecordId || recoveryOptions.forDeposit) throw error;
          addLog(
            `Encrypted Solana recovery restore is unavailable: ${error instanceof Error ? error.message : String(error)}; checking available local records`,
            "warning",
          );
        }
        assertCurrent();
        const conflicts = new Set<string>();
        for (const [id, variants] of recovery.variants) {
          const identities = new Set(
            variants.map((item) => settlementIntentIdentity(item.record)),
          );
          try {
            const local = await getSolanaJournalRecord(journalScope, id);
            if (local?.intent) identities.add(settlementIntentIdentity(local));
          } catch {
            conflicts.add(id);
          }
          if (identities.size > 1) conflicts.add(id);
        }
        const selectedVariant =
          resumeRecordId && resumeBackupId
            ? recovery.variants
                .get(resumeRecordId)
                ?.find((item) => item.backupId === resumeBackupId)
            : undefined;
        if (resumeBackupId && !selectedVariant) {
          throw new Error(
            "Selected encrypted Solana recovery version is unavailable",
          );
        }
        const mpkHex = formatMpkHex(spendingKeys.mpk);
        const recipientKey = await hashMpkHex(mpkHex);
        assertCurrent();
        const viewingKeys = getInboxViewingKeyCandidates(
          spendingKeys.spendingKey,
        );
        const inboxSecrets = getInboxSecretKeyCandidates(
          spendingKeys.spendingKey,
        );
        const fetchInbox = async () => {
          const headers = await authHeadersForPath("/api/v1/notes", {
            expectedRecipientKey: recipientKey,
          });
          assertCurrent();
          return withDeadline(
            async (signal) => {
              const response = await fetch(
                buildEngineUrl(notesApiUrl, `/api/v1/notes/${recipientKey}`),
                {
                  headers: { "Content-Type": "application/json", ...headers },
                  signal,
                },
              );
              return {
                ok: response.ok,
                status: response.status,
                data: response.ok
                  ? ((await response.json()) as { notes?: unknown[] })
                  : null,
              };
            },
            "Encrypted inbox recovery timed out; retry recovery",
            undefined,
            30_000,
          );
        };
        let response = await fetchInbox();
        assertCurrent();
        if (response.status === 401 || response.status === 403) {
          clearProxyAuthToken();
          response = await fetchInbox();
          assertCurrent();
        }
        let imported: StoredJoinSplitNote[] = [];
        if (response.ok) {
          const data = response.data!;
          if (recoveryOptions.forDeposit && !Array.isArray(data.notes)) {
            throw new Error(
              "Deposit paused: encrypted inbox returned an invalid recovery response",
            );
          }
          const decrypted = await Promise.all(
            (data.notes || []).map(async (enc) => {
              try {
                const decrypted = await decryptInboxNote(
                  enc as Parameters<typeof decryptInboxNote>[0],
                  viewingKeys,
                  mpkHex,
                  inboxSecrets,
                );
                const note = restoreSolanaInboxNamespace(decrypted, network);
                return isSolanaFlatNote(note, network) ? note : null;
              } catch {
                return null;
              }
            }),
          );
          imported = decrypted
            .filter((note): note is JoinSplitNote => Boolean(note))
            // Mailbox leaf metadata is never trusted; sync assigns positions.
            .map((note) => ({
              note: { ...note, leafIndex: -1 },
              timestamp: Date.now(),
            }));
        } else {
          if (recoveryOptions.forDeposit) {
            throw new Error(
              `Deposit paused: encrypted inbox is unavailable (${response.status}); recovery must complete before depositing`,
            );
          }
          addLog(
            `Inbox fetch failed (${response.status}); using local notes only`,
            "warning",
          );
        }
        // An empty or failed mailbox response must never erase local notes.
        assertCurrent();
        const candidates = mergeNotes(notesRef.current, imported);
        const result = await synchronizeSolanaNotes({
          rpc,
          network,
          spendingKeys,
          notes: candidates,
          cache,
        });
        assertCurrent();
        const next = candidates.map((stored) => {
          const key = stored.note.commitment.toLowerCase();
          const validated = result.validated.get(key);
          if (validated) return { ...validated, spent: false };
          if (result.spent.has(key)) return { ...stored, spent: true };
          return stored;
        });
        // Preserve notes added or spent while the chain request was in flight.
        await commitNotes((current) => mergeNotes(current, next));
        for (const [commitment, reason] of result.rejected) {
          if (
            !notesRef.current.some(
              (stored) =>
                stored.note.commitment.toLowerCase() === commitment &&
                stored.spent,
            )
          ) {
            addLog(
              `Note ${commitment.slice(0, 10)}… was rejected: ${reason}`,
              "warning",
            );
          }
        }
        const reconciled = await reconcileSolanaJournal({
          rpc,
          network,
          scope: journalScope,
          relay,
          backupJournal,
          backupVariants: recovery.variants,
          resumeRecordId:
            resumeRecordId &&
            (!conflicts.has(resumeRecordId) || selectedVariant)
              ? resumeRecordId
              : undefined,
          resumeVariant: selectedVariant,
          onLog: (...args) => {
            if (isCurrent()) addLog(...args);
          },
        });
        assertCurrent();
        if (
          reconciled.spentCommitments.length > 0 ||
          reconciled.finalizedNotes.length > 0
        ) {
          // A fresh device can recover a deposit and its later spend together.
          // Import and mark them in one update: marking an absent input before
          // importing it would resurrect it as an unspent note.
          const spent = new Set(
            reconciled.spentCommitments.map((c) => c.toLowerCase()),
          );
          await commitNotes((current) =>
            mergeNotes(
              current,
              reconciled.finalizedNotes.map((note) => ({
                note,
                timestamp: Date.now(),
                spent: false,
              })),
            ).map((stored) =>
              spent.has(stored.note.commitment.toLowerCase())
                ? { ...stored, spent: true }
                : stored,
            ),
          );
        }
        if (reconciled.deliveryPending.length > 0)
          await completeDeliveries(reconciled.deliveryPending);
        assertCurrent();
        // A finalized swap may have lost its relay response before the action
        // handler wrote Activity. Its encrypted journal retains the input
        // amount and transaction identity after balance recovery completes.
        const recoveredRecords = await listSolanaJournal(journalScope, {
          skipUnreadable: true,
        });
        assertCurrent();
        for (const record of recoveredRecords) {
          if (
            record.kind !== "swap" ||
            record.state !== "completed" ||
            !record.signature ||
            record.intent?.kind !== "swap"
          )
            continue;
          const token = network.tokens.find(
            (candidate) => candidate.mint === record.intent!.mint,
          );
          if (!token) continue;
          const entry = {
            type: "swap" as const,
            amount: formatSolanaTokenAmount(
              BigInt(record.intent.withdrawAmount),
              token.decimals,
            ),
            symbol: token.symbol,
            txHash: record.signature,
          };
          if (
            !historyRef.current.some(
              (existing) => historyKey(existing) === historyKey(entry),
            )
          )
            await addHistoryEntry(entry, record.createdAt);
          assertCurrent();
        }
        for (const failure of reconciled.failed) {
          addLog(`A pending settlement failed: ${failure.error}`, "error");
        }
        setConflictedSubmissionIds(
          [...conflicts].filter((id) => reconciled.stillPending.includes(id)),
        );
        const options: Record<string, SolanaJournalBackupVariant[]> = {};
        for (const id of conflicts) {
          if (!reconciled.stillPending.includes(id)) continue;
          const unique = new Map<string, SolanaJournalBackupVariant>();
          for (const item of recovery.variants.get(id) ?? []) {
            unique.set(settlementIntentIdentity(item.record), item);
          }
          options[id] = [...unique.values()];
        }
        setConflictedSubmissionOptions(options);
        setPendingSubmissions(
          (
            await activeSolanaJournalRecords(journalScope, {
              skipUnreadable: true,
            })
          ).filter(
            (record) =>
              record.kind !== "deposit" &&
              record.kind !== "deposit_merge" &&
              record.state !== "watching" &&
              (record.restoredFromBackup === true ||
                (record.submissionAttempted !== true && !record.signature)) &&
              record.state !== "finalized" &&
              record.state !== "delivery_pending",
          ),
        );
        const available = notesRef.current.filter(
          ({ note, spent }) =>
            !spent && note.leafIndex >= 0 && isSolanaFlatNote(note, network),
        );
        const amounts = network.tokens.flatMap((token) => {
          const value = available.reduce(
            (sum, { note }) =>
              solanaNoteMint(note) === token.mint ? sum + note.value : sum,
            0n,
          );
          return value > 0n
            ? [
                `${formatSolanaTokenAmount(value, token.decimals)} ${token.symbol}`,
              ]
            : [];
        });
        const pending = notesRef.current.filter(
          ({ note, spent }) => !spent && note.leafIndex < 0,
        ).length;
        const spent = notesRef.current.filter((stored) => stored.spent).length;
        // Note finality and transaction recovery are separate: a cancelled or
        // unresolved deposit can have a journal record without an output note.
        const active = await activeSolanaJournalRecords(journalScope, {
          skipUnreadable: !recoveryOptions.forDeposit,
        });
        assertCurrent();
        const unresolved = new Set([
          ...reconciled.stillPending,
          ...active
            .filter(
              (record) =>
                record.state === "prepared" ||
                record.state === "broadcast" ||
                record.state === "failed",
            )
            .map((record) => record.id),
        ]);
        addLog(
          `Recovery complete: ${available.length} available private note${available.length === 1 ? "" : "s"}${amounts.length ? ` (${amounts.join(", ")})` : ""}; ${pending} pending notes; ${spent} spent; ${unresolved.size} unresolved transaction${unresolved.size === 1 ? "" : "s"}`,
          unresolved.size > 0 ? "warning" : "success",
        );
        for (const id of unresolved) {
          const record = recoveredRecords.find((item) => item.id === id);
          const reason = record?.error
            ? record.error
            : record?.signature
              ? "finalized transaction outcome has not been verified"
              : "saved attempt has no confirmed transaction; only its affected notes remain reserved";
          addLog(
            `Unresolved ${record?.kind.replace(/_/g, " ") ?? "transaction"} ${id.slice(0, 8)}: ${reason}`,
            "warning",
            record?.signature,
          );
        }
        if (reconciled.watchOnly?.length)
          addLog(
            `Recovery is watching ${reconciled.watchOnly.length} older attempt${reconciled.watchOnly.length === 1 ? "" : "s"} for receipts; they do not reserve spendable notes`,
            "info",
          );
        if (recoveryOptions.forDeposit) {
          // A live first deposit of the same asset must finalize before top-up.
          // Other attempts reserve their own input notes, not the whole wallet.
          const pendingDeposits = active.filter(
            (record) =>
              unresolved.has(record.id) &&
              record.kind === "deposit" &&
              record.deliveries.some((delivery) => delivery.keepLocal) &&
              record.deposit?.mint === (recoveryOptions.mint ?? null),
          );
          if (pendingDeposits.length > 0) {
            throw new Error(
              `Deposit paused: ${pendingDeposits.length} previous deposit${pendingDeposits.length === 1 ? " needs" : "s need"} recovery for this asset; its signing window will be checked on refresh`,
            );
          }
          if (
            notesRef.current.some(
              ({ note, spent }) =>
                !spent &&
                note.leafIndex < 0 &&
                solanaNoteMint(note) === (recoveryOptions.mint ?? null),
            )
          ) {
            throw new Error(
              "Deposit paused: private notes are awaiting finalized recovery; refresh recovery before retrying",
            );
          }
        }
        return { ...reconciled, notes: [...notesRef.current] };
      } catch (error) {
        if (!isCurrent()) return;
        addLog(
          `Note sync failed: ${error instanceof Error ? error.message : String(error)}`,
          "error",
        );
        if (resumeRecordId || recoveryOptions.forDeposit) throw error;
      } finally {
        context.syncing = false;
        finishSync();
        if (isCurrent()) setSyncing(false);
      }
    },
    [
      addLog,
      addHistoryEntry,
      assertCurrent,
      context,
      isCurrent,
      authHeadersForPath,
      backupJournal,
      cache,
      clearProxyAuthToken,
      commitNotes,
      completeDeliveries,
      journalScope,
      markSpentLocally,
      network,
      notesApiUrl,
      relay,
      rememberLocalNotes,
      restoreJournal,
      rpc,
      spendingKeys,
      wallet.address,
    ],
  );

  const syncHistory = useCallback(async () => {
    if (!spendingKeys || !isCurrent()) return;
    const revision = ++context.historySync;
    const current = () => isCurrent() && revision === context.historySync;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      await context.ready;
      if (!current()) return;
      const mpkHex = formatMpkHex(spendingKeys.mpk);
      const recipientKey = await hashMpkHex(mpkHex);
      if (!current()) return;
      const headers = await authHeadersForPath("/api/v1/history", {
        expectedRecipientKey: recipientKey,
      });
      if (!current()) return;
      const entries = await withDeadline(
        () =>
          fetchPrivateHistoryEntries({
            baseUrl: notesApiUrl,
            recipientKey,
            headers,
            decryptContext: {
              viewingKeyHex: getInboxViewingKeyCandidates(
                spendingKeys.spendingKey,
              ),
              legacyMpkHex: mpkHex,
              inboxSecretKey: getInboxSecretKeyCandidates(
                spendingKeys.spendingKey,
              ),
            },
          }),
        "History refresh timed out; retry refresh",
        undefined,
        30_000,
      );
      if (!current()) return;
      const remote: SolanaHistoryEntry[] = entries
        .filter(
          (entry) =>
            entry.network === undefined || entry.network === network.key,
        )
        .map((entry, index) => ({
          id: Date.now() + index,
          type: ["deposit", "transfer", "withdraw", "receive", "swap"].includes(
            String(entry.type),
          )
            ? (entry.type as SolanaHistoryEntry["type"])
            : "transfer",
          amount: typeof entry.amount === "string" ? entry.amount : "0",
          symbol:
            typeof entry.token === "string"
              ? entry.token
              : typeof entry.symbol === "string"
                ? entry.symbol
                : "SOL",
          memo:
            typeof entry.memo === "string" && entry.memo.trim()
              ? entry.memo
              : undefined,
          txHash: typeof entry.txHash === "string" ? entry.txHash : undefined,
          timestamp: entry.timestamp
            ? Number(entry.timestamp) *
              (Number(entry.timestamp) < 1_000_000_000_000 ? 1000 : 1)
            : Date.now(),
        }));
      const remoteKeys = new Set(remote.map(historyKey));
      const recentLocal = historyRef.current.filter(
        (entry) =>
          !remoteKeys.has(historyKey(entry)) &&
          Date.now() - entry.timestamp < 30 * 60_000,
      );
      await commitHistory([...recentLocal, ...remote]);
    } catch (error) {
      if (current())
        setHistoryError(error instanceof Error ? error.message : String(error));
    } finally {
      if (current()) setHistoryLoading(false);
    }
  }, [
    authHeadersForPath,
    commitHistory,
    context,
    isCurrent,
    network.key,
    notesApiUrl,
    spendingKeys,
  ]);

  // Load the encrypted caches on connect; clear everything on disconnect.
  useEffect(() => {
    let cancelled = false;
    setNotes([]);
    setHistory([]);
    setSyncing(false);
    setHistoryLoading(false);
    setHistoryError(null);
    setPendingSubmissions([]);
    setConflictedSubmissionIds([]);
    setConflictedSubmissionOptions({});
    if (!cacheScope) {
      notesRef.current = [];
      historyRef.current = [];
      setNotes([]);
      setHistory([]);
      return;
    }
    context.ready = (async () => {
      const [cachedNotes, cachedHistory, journalRecords] = await Promise.all([
        loadSolanaNotesCache(cacheScope),
        loadSolanaHistoryCache(cacheScope),
        journalScope
          ? listSolanaJournal(journalScope, {
              skipUnreadable: true,
              onUnreadable: (count) =>
                addLogRef.current(
                  `Skipped ${count} unreadable local Solana recovery record${count === 1 ? "" : "s"}; trying encrypted backups`,
                  "warning",
                ),
            })
          : Promise.resolve([]),
      ]);
      if (cancelled || !isCurrent()) return;
      // Older clients completed deposits before saving the local note. Recover
      // those encrypted secrets too, but validate ownership/nullifiers on chain
      // before making them spendable (including already spent deposits).
      const recovered = journalRecords
        .filter(
          (record) => record.kind === "deposit" && record.state === "completed",
        )
        .flatMap((record) =>
          record.outputNotes
            .filter((note) =>
              record.deliveries.some(
                (delivery) =>
                  delivery.keepLocal &&
                  delivery.commitment.toLowerCase() ===
                    note.commitment.toLowerCase(),
              ),
            )
            .map((note) => ({
              note,
              timestamp: record.createdAt,
              txHash: record.signature,
            })),
        );
      // Cached leaf positions render balances but are re-validated before spending.
      const displayOnly = mergeNotes(cachedNotes, recovered).map((stored) => ({
        ...stored,
        note: { ...stored.note, leafIndex: -1 },
      }));
      notesRef.current = displayOnly;
      historyRef.current = cachedHistory;
      setNotes(displayOnly);
      setHistory(cachedHistory);
    })();
    void context.ready.catch((error) => {
      if (!cancelled && isCurrent())
        addLogRef.current(
          `Solana recovery cache could not be loaded: ${String(error)}`,
          "error",
        );
    });
    return () => {
      cancelled = true;
    };
  }, [cacheScope, context, journalScope, isCurrent]);

  const balances = useMemo<SolanaBalanceRow[]>(() => {
    return network.tokens.map((token) => {
      let spendable = 0n;
      let pending = 0n;
      for (const stored of notes) {
        if (stored.spent || !isSolanaFlatNote(stored.note, network)) continue;
        let mint: string | null;
        try {
          mint = solanaNoteMint(stored.note);
        } catch {
          continue;
        }
        if (mint !== token.mint) continue;
        if (stored.note.leafIndex >= 0) spendable += stored.note.value;
        else pending += stored.note.value;
      }
      return {
        mint: token.mint,
        symbol: token.symbol,
        decimals: token.decimals,
        spendable,
        pending,
        spendableDisplay: formatSolanaTokenAmount(spendable, token.decimals),
        pendingDisplay: formatSolanaTokenAmount(pending, token.decimals),
      };
    });
  }, [network, notes]);

  const tokenOf = useCallback(
    (mint: string | null) => findSolanaToken(network, mint),
    [network],
  );

  return {
    notes,
    history,
    balances,
    syncing,
    historyLoading,
    historyError,
    pendingSubmissions,
    conflictedSubmissionIds,
    conflictedSubmissionOptions,
    journalScope,
    relay,
    backupJournal,
    cache,
    syncNotes,
    syncHistory,
    rememberLocalNotes,
    rememberSettlement,
    markSpentLocally,
    addHistoryEntry,
    completeDeliveries,
    tokenOf,
  };
}
